import { useState, useEffect } from 'react';
import { Bell, Check, Loader2, MessageSquare, ExternalLink, Package, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OrderDetailsModal } from '@/components/OrderDetailsModal';
import { QuickReviewModal } from '@/components/QuickReviewModal';
import { AdminReplyModal } from '@/components/AdminReplyModal';

interface Notification {
  id: string;
  type: string;
  message: string;
  read: boolean;
  created_at: string;
  review_id: string | null;
  product_id: string | null;
  product_name: string | null;
  order_id: string | null;
}

interface OrderProduct {
  id: string;
  name: string;
  image_url: string | null;
}

export const NotificationsDropdown = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [orderProducts, setOrderProducts] = useState<OrderProduct[]>([]);
  const [selectedOrderIdForReview, setSelectedOrderIdForReview] = useState<string | null>(null);
  const [adminReplyModalOpen, setAdminReplyModalOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (user) {
      fetchNotifications();
      
      // Subscribe to realtime notifications
      const channel = supabase
        .channel('notifications-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            // Add new notification to the list
            const newNotification = payload.new as any;
            setNotifications(prev => [
              {
                ...newNotification,
                product_name: null, // Will be fetched on next open
              },
              ...prev,
            ]);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const { data: notificationsData, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      if (!notificationsData || notificationsData.length === 0) {
        setNotifications([]);
        return;
      }

      // Fetch product names for notifications with product_id
      const productIds = [...new Set(
        notificationsData
          .filter(n => n.product_id)
          .map(n => n.product_id)
      )];

      let productsMap = new Map<string, string>();
      
      if (productIds.length > 0) {
        const { data: productsData } = await supabase
          .from('products')
          .select('id, name')
          .in('id', productIds);

        productsMap = new Map(
          (productsData || []).map(p => [p.id, p.name])
        );
      }

      const enrichedNotifications: Notification[] = notificationsData.map(n => ({
        id: n.id,
        type: n.type,
        message: n.message,
        read: n.read,
        created_at: n.created_at,
        review_id: n.review_id,
        product_id: n.product_id,
        product_name: n.product_id ? productsMap.get(n.product_id) || null : null,
        order_id: n.order_id || null,
      }));

      setNotifications(enrichedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    markAsRead(notification.id);
    
    // Order status notification - open order details modal
    if (notification.type === 'order_status' && notification.order_id) {
      setSelectedOrderId(notification.order_id);
      setOrderModalOpen(true);
      setIsOpen(false);
      return;
    }
    
    // Order completed - open review modal with order products
    if (notification.type === 'order_completed' && notification.order_id) {
      try {
        const { data: items } = await supabase
          .from('order_items')
          .select('product_id, product_name, product_image_url')
          .eq('order_id', notification.order_id);

        if (items && items.length > 0) {
          // Get user's existing reviews to filter out already reviewed products
          const { data: reviews } = await supabase
            .from('reviews')
            .select('product_id')
            .eq('user_id', user!.id);

          const reviewedIds = new Set((reviews || []).map(r => r.product_id));
          
          const unratedProducts = items
            .filter(item => item.product_id && !reviewedIds.has(item.product_id))
            .map(item => ({
              id: item.product_id!,
              name: item.product_name,
              image_url: item.product_image_url,
            }));

          if (unratedProducts.length > 0) {
            setOrderProducts(unratedProducts);
            setSelectedOrderIdForReview(notification.order_id);
            setReviewModalOpen(true);
            setIsOpen(false);
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching order products:', error);
      }
      // Fallback: open order details if no products to review
      setSelectedOrderId(notification.order_id);
      setOrderModalOpen(true);
      setIsOpen(false);
      return;
    }
    
    // Review reply notification - open admin reply modal
    if (notification.type === 'admin_reply' && notification.review_id) {
      setSelectedReviewId(notification.review_id);
      setSelectedProductName(notification.product_name);
      setAdminReplyModalOpen(true);
      setIsOpen(false);
      return;
    }

    // Fallback for other product notifications
    if (notification.product_id) {
      setIsOpen(false);
      navigate('/catalog');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;
    
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    });
  };

  if (!user) return null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <motion.div whileTap={{ scale: 0.95 }}>
          <Button
            variant="ghost"
            size="icon"
            className="relative rounded-full hover:bg-secondary"
          >
            <Bell className="h-5 w-5" />
            <AnimatePresence>
              {unreadCount > 0 && (
                <motion.div
                  className="absolute -top-1 -right-1"
                  initial={{ scale: 0 }}
                  animate={{ scale: [1, 1.2, 1] }}
                  exit={{ scale: 0 }}
                  transition={{ 
                    scale: { 
                      repeat: Infinity, 
                      repeatType: "loop", 
                      duration: 2,
                      ease: "easeInOut"
                    }
                  }}
                >
                  <Badge
                    className="h-5 w-5 flex items-center justify-center p-0 text-xs bg-primary text-primary-foreground border-0"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </motion.div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-card border-border p-0">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <h3 className="font-medium">Уведомления</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
            >
              <Check className="h-3 w-3 mr-1" />
              Прочитать все
            </Button>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Bell className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">Нет уведомлений</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[300px]">
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`p-3 cursor-pointer transition-colors hover:bg-muted/50 ${
                    !notification.read ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex gap-3">
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      notification.type === 'order_completed' 
                        ? 'bg-yellow-500/10' 
                        : notification.type === 'order_status' 
                          ? 'bg-eco/10' 
                          : 'bg-primary/10'
                    }`}>
                      {notification.type === 'order_completed' ? (
                        <Star className="h-4 w-4 text-yellow-500" />
                      ) : notification.type === 'order_status' ? (
                        <Package className="h-4 w-4 text-eco" />
                      ) : (
                        <MessageSquare className="h-4 w-4 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{notification.message}</p>
                      {notification.product_name && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          Товар: {notification.product_name}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </ScrollArea>
        )}
        
        {notifications.length > 0 && (
          <div className="p-2 border-t border-border">
            <Link to="/notifications" onClick={() => setIsOpen(false)}>
              <Button variant="ghost" size="sm" className="w-full justify-center gap-1 text-muted-foreground">
                Все уведомления
                <ExternalLink className="h-3 w-3" />
              </Button>
            </Link>
          </div>
        )}
      </DropdownMenuContent>

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={orderModalOpen}
        onOpenChange={setOrderModalOpen}
        orderId={selectedOrderId}
      />

      {/* Quick Review Modal */}
      <QuickReviewModal
        open={reviewModalOpen}
        onOpenChange={setReviewModalOpen}
        products={orderProducts}
        orderId={selectedOrderIdForReview || undefined}
        onReviewSubmitted={fetchNotifications}
      />

      {/* Admin Reply Modal */}
      <AdminReplyModal
        open={adminReplyModalOpen}
        onOpenChange={setAdminReplyModalOpen}
        reviewId={selectedReviewId}
        productName={selectedProductName}
      />
    </DropdownMenu>
  );
};
